import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronDown, ChevronUp, GripHorizontal } from 'lucide-react';
import { DEFAULT_COLOR } from '../constants/colors';
import { ColorPicker } from './shared/ColorPicker';
import { HighlightSaveSection } from './shared/HighlightSaveSection';
const DEFAULT_LINE_WIDTH = 3;
// Module-level variable to persist user's unit preference across dialog instances
let userPreferredUnit = null;
// Shared with other highlight dialogs (e.g. stream) so the user's ft/m
// preference persists across them.
export const getPreferredUnit = () => userPreferredUnit;
export const setPreferredUnit = (unit) => { userPreferredUnit = unit; };
const parseIntervalUnit = (interval) => {
    if (interval.toLowerCase().includes('ft'))
        return 'ft';
    return 'm';
};
const parseElevation = (elevation) => {
    const ftMatch = elevation.match(/^(-?[\d.]+)\s*ft$/i);
    if (ftMatch)
        return { value: parseFloat(ftMatch[1]), unit: 'ft' };
    const mMatch = elevation.match(/^(-?[\d.]+)\s*m$/i);
    if (mMatch)
        return { value: parseFloat(mMatch[1]), unit: 'm' };
    const numMatch = elevation.match(/^(-?[\d.]+)$/);
    if (numMatch)
        return { value: parseFloat(numMatch[1]), unit: 'm' };
    return null;
};
const convertElevation = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit)
        return value;
    if (fromUnit === 'ft' && toUnit === 'm')
        return value * 0.3048;
    if (fromUnit === 'm' && toUnit === 'ft')
        return value / 0.3048;
    return value;
};
const formatElevation = (value, unit) => {
    const formatted = Math.abs(value) < 10 ? value.toFixed(2) : Math.abs(value) < 100 ? value.toFixed(1) : Math.round(value).toString();
    return `${formatted} ${unit}`;
};
export const ContourHighlightDialog = ({ isOpen, contourInterval, contourElevation, screenPosition, onSaveToFolder, onSaveLater, onDiscard, userFolders, onCreateFolder, isSavePending, onColorPreview, onLineWidthPreview, initialColor, initialLineWidth, initialName, initialPartial, isClosedContour, onPartialModeChange, onFlipSide, isServerArtifactEdit, currentFolderId }) => {
    const [selectedColor, setSelectedColor] = useState(initialColor !== null && initialColor !== void 0 ? initialColor : DEFAULT_COLOR);
    const [highlightName, setHighlightName] = useState(initialName !== null && initialName !== void 0 ? initialName : '');
    const [selectedLineWidth, setSelectedLineWidth] = useState(initialLineWidth !== null && initialLineWidth !== void 0 ? initialLineWidth : DEFAULT_LINE_WIDTH);
    const [isPartial, setIsPartial] = useState(initialPartial !== null && initialPartial !== void 0 ? initialPartial : false);
    const handlePartialChange = useCallback((partial) => {
        setIsPartial(partial);
        onPartialModeChange === null || onPartialModeChange === void 0 ? void 0 : onPartialModeChange(partial);
    }, [onPartialModeChange]);
    const handleColorPick = useCallback((color) => {
        setSelectedColor(color);
        onColorPreview === null || onColorPreview === void 0 ? void 0 : onColorPreview(color);
    }, [onColorPreview]);
    const handleLineWidthChange = useCallback((width) => {
        setSelectedLineWidth(width);
        onLineWidthPreview === null || onLineWidthPreview === void 0 ? void 0 : onLineWidthPreview(width);
    }, [onLineWidthPreview]);
    // Unit handling
    const intervalUnit = parseIntervalUnit(contourInterval);
    const defaultUnit = userPreferredUnit || intervalUnit;
    const [unitMode, setUnitMode] = useState(defaultUnit);
    const hasUserSelected = useRef(false);
    // Drag state
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isCollapsed, setIsCollapsed] = useState(false);
    const windowRef = useRef(null);
    // True once we've initialised state for this open session — prevents
    // the open-effect from re-firing (and resetting position) when the
    // user picks a colour or width during the session and the parent's
    // initial* props change as a result.
    const initializedThisOpenRef = useRef(false);
    // Update unit when interval changes, but only if user hasn't manually selected
    useEffect(() => {
        if (!hasUserSelected.current && !userPreferredUnit) {
            setUnitMode(intervalUnit);
        }
    }, [intervalUnit]);
    // Reset state when dialog opens. Runs ONCE per open session — the
    // initializedThisOpenRef guard means subsequent prop changes during
    // the same open (e.g. colour preview updating editingDefaults) don't
    // re-fire this and clobber the user's drag-positioned dialog or any
    // local edits.
    useEffect(() => {
        if (!isOpen) {
            initializedThisOpenRef.current = false;
            return;
        }
        if (initializedThisOpenRef.current)
            return;
        initializedThisOpenRef.current = true;
        const newDefault = userPreferredUnit || intervalUnit;
        setUnitMode(newDefault);
        hasUserSelected.current = false;
        setIsCollapsed(false);
        setHighlightName(initialName !== null && initialName !== void 0 ? initialName : '');
        setSelectedColor(initialColor !== null && initialColor !== void 0 ? initialColor : DEFAULT_COLOR);
        setSelectedLineWidth(initialLineWidth !== null && initialLineWidth !== void 0 ? initialLineWidth : DEFAULT_LINE_WIDTH);
        setIsPartial(initialPartial !== null && initialPartial !== void 0 ? initialPartial : false);
        // Re-opening over an already-partial highlight: re-arm the drag handles
        // so the user can adjust the span immediately, without toggling.
        if (initialPartial)
            onPartialModeChange === null || onPartialModeChange === void 0 ? void 0 : onPartialModeChange(true);
        // Initialize position from screenPosition
        const dialogWidth = 340;
        const dialogHeight = 220;
        const padding = 16;
        const bottomControlsHeight = 80;
        let left = screenPosition.x + 10;
        let top = screenPosition.y - 10;
        if (left + dialogWidth + padding > window.innerWidth) {
            left = screenPosition.x - dialogWidth - 10;
        }
        if (top + dialogHeight + padding + bottomControlsHeight > window.innerHeight) {
            top = window.innerHeight - dialogHeight - padding - bottomControlsHeight;
        }
        left = Math.max(padding, left);
        top = Math.max(padding, top);
        setPosition({ x: left, y: top });
    }, [isOpen, intervalUnit, screenPosition, initialColor, initialLineWidth, initialName, initialPartial]);
    const handleUnitChange = (unit) => {
        setUnitMode(unit);
        userPreferredUnit = unit;
        hasUserSelected.current = true;
    };
    const parsed = contourElevation ? parseElevation(contourElevation) : null;
    const getDisplayElevation = () => {
        if (!parsed || !contourElevation)
            return contourElevation || '';
        const convertedValue = convertElevation(parsed.value, parsed.unit, unitMode);
        return formatElevation(convertedValue, unitMode);
    };
    // Drag handlers
    const handleMouseDown = useCallback((e) => {
        if (windowRef.current) {
            const rect = windowRef.current.getBoundingClientRect();
            setDragOffset({
                x: e.clientX - rect.left,
                y: e.clientY - rect.top
            });
            setIsDragging(true);
        }
    }, []);
    const handleMouseMove = useCallback((e) => {
        if (isDragging) {
            const dialogWidth = 340;
            const newX = Math.max(0, Math.min(window.innerWidth - dialogWidth, e.clientX - dragOffset.x));
            const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
            setPosition({ x: newX, y: newY });
        }
    }, [isDragging, dragOffset]);
    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);
    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, handleMouseMove, handleMouseUp]);
    if (!isOpen)
        return null;
    const dialogWidth = 340;
    const isEditMode = initialColor !== undefined || initialName !== undefined || initialLineWidth !== undefined;
    const collectValues = () => ({
        color: selectedColor,
        name: highlightName.trim() || undefined,
        lineWidth: selectedLineWidth
    });
    return (_jsxs("div", { ref: windowRef, className: "fixed z-[70] rounded-lg shadow-xl", style: {
            left: position.x,
            top: position.y,
            width: dialogWidth,
            backgroundColor: 'var(--charcoal)',
            border: '1px solid var(--pewter)',
            userSelect: isDragging ? 'none' : 'auto'
        }, children: [_jsxs("div", { className: "flex items-center justify-between px-3 py-2 cursor-move rounded-t-lg", style: { backgroundColor: 'var(--pewter)' }, onMouseDown: handleMouseDown, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx(GripHorizontal, { size: 14, style: { color: 'var(--dew)', opacity: 0.5 } }), _jsx("span", { className: "text-sm font-semibold", style: { color: 'var(--sand)' }, children: "Contour" }), isCollapsed && (_jsx("div", { className: "w-3 h-3 rounded border", style: {
                                    backgroundColor: selectedColor,
                                    borderColor: selectedColor === '#FFFFFF' ? 'var(--pewter)' : selectedColor
                                } }))] }), _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("button", { onClick: () => setIsCollapsed(!isCollapsed), className: "p-1 rounded hover:bg-opacity-20 hover:bg-gray-500 transition-colors", style: { color: 'var(--dew)' }, onMouseDown: (e) => e.stopPropagation(), "aria-label": isCollapsed ? 'Expand' : 'Collapse', children: isCollapsed ? _jsx(ChevronDown, { size: 16 }) : _jsx(ChevronUp, { size: 16 }) }), _jsx("button", { onClick: () => {
                                    // Server-edit X reverts (revert + show server back). The
                                    // regular "Save later" semantics would create a duplicate
                                    // Unsaved Drawing while the saved artifact reappears
                                    // un-edited.
                                    if (isServerArtifactEdit) {
                                        onDiscard();
                                    }
                                    else {
                                        onSaveLater(collectValues());
                                    }
                                }, className: "p-1 rounded hover:bg-opacity-20 hover:bg-gray-500 transition-colors", style: { color: 'var(--dew)' }, onMouseDown: (e) => e.stopPropagation(), "aria-label": "Close", title: isServerArtifactEdit ? 'Cancel edit' : 'Save later (keep in Unsaved Drawings)', children: _jsx(X, { size: 16 }) })] })] }), !isCollapsed && (_jsxs("div", { className: "p-4", children: [_jsxs("div", { className: "mb-3 flex items-center gap-3", children: [_jsx("div", { className: "text-xs flex-shrink-0", style: { color: 'var(--dew)', minWidth: 70 }, children: "Label" }), _jsx("input", { type: "text", value: highlightName, onChange: (e) => setHighlightName(e.target.value), placeholder: `Contour at ${contourElevation || 'unknown'}`, className: "flex-1 min-w-0 px-2 py-1 rounded text-sm", style: {
                                    backgroundColor: 'var(--pewter)',
                                    color: 'var(--sand)',
                                    border: '1px solid var(--charcoal)'
                                } })] }), contourElevation && (_jsxs("div", { className: "mb-3 flex items-center gap-3", children: [_jsx("div", { className: "text-xs flex-shrink-0", style: { color: 'var(--dew)', minWidth: 70 }, children: "Elevation" }), _jsx("span", { className: "text-sm font-semibold flex-1", style: { color: 'var(--sand)' }, children: getDisplayElevation() }), parsed && (_jsxs("select", { value: unitMode, onChange: (e) => handleUnitChange(e.target.value), className: "px-2 py-1 rounded text-xs cursor-pointer", style: {
                                    backgroundColor: 'var(--pewter)',
                                    color: 'var(--sand)',
                                    border: '1px solid var(--charcoal)',
                                }, title: "Display unit", children: [_jsx("option", { value: "m", children: "m" }), _jsx("option", { value: "ft", children: "ft" })] }))] })), _jsxs("div", { className: "mb-3 flex items-center gap-3", children: [_jsx("div", { className: "text-xs flex-shrink-0", style: { color: 'var(--dew)', minWidth: 70 }, children: "Span" }), _jsxs("div", { className: "flex-1 min-w-0 flex items-center gap-2", children: [_jsx("div", { className: "flex rounded overflow-hidden", style: { border: '1px solid var(--pewter)' }, children: ['whole', 'partial'].map((mode) => {
                                            const active = (mode === 'partial') === isPartial;
                                            return (_jsx("button", { onClick: () => handlePartialChange(mode === 'partial'), className: "px-3 py-1 text-xs capitalize transition-colors", style: {
                                                    backgroundColor: active ? 'var(--sand)' : 'var(--pewter)',
                                                    color: active ? 'var(--charcoal)' : 'var(--sand)',
                                                    fontWeight: active ? 600 : 400,
                                                }, children: mode }, mode));
                                        }) }), isPartial && isClosedContour && (_jsx("button", { onClick: () => onFlipSide === null || onFlipSide === void 0 ? void 0 : onFlipSide(), className: "px-2 py-1 rounded text-xs transition-colors", style: {
                                            backgroundColor: 'var(--pewter)',
                                            color: 'var(--sand)',
                                            border: '1px solid var(--charcoal)',
                                        }, title: "Highlight the other arc of this closed contour", children: "Flip side" }))] })] }), isPartial && (_jsx("div", { className: "mb-3 text-xs", style: { color: 'var(--dew)', paddingLeft: 82 }, children: "Drag the two handles on the contour to set the highlighted portion." })), _jsxs("div", { className: "mb-3 flex items-center gap-3", children: [_jsx("div", { className: "text-xs flex-shrink-0", style: { color: 'var(--dew)', minWidth: 70 }, children: "Color" }), _jsx("div", { className: "flex-1 min-w-0", children: _jsx(ColorPicker, { selectedColor: selectedColor, onColorChange: handleColorPick }) })] }), _jsxs("div", { className: "mb-3 flex items-center gap-3", children: [_jsx("div", { className: "text-xs flex-shrink-0", style: { color: 'var(--dew)', minWidth: 70 }, children: "Line width" }), _jsx("input", { type: "range", min: 1, max: 8, step: 1, value: selectedLineWidth, onChange: (e) => handleLineWidthChange(parseInt(e.target.value, 10)), className: "flex-1 min-w-0" }), _jsxs("span", { className: "text-xs flex-shrink-0 tabular-nums", style: { color: 'var(--sand)', minWidth: 36, textAlign: 'right' }, children: [selectedLineWidth, " px"] })] }), _jsx(HighlightSaveSection, { userFolders: userFolders, onCreateFolder: onCreateFolder, onSaveToFolder: (folderId) => onSaveToFolder(folderId, collectValues()), onSaveLater: () => onSaveLater(collectValues()), onDiscard: onDiscard, confirmDiscard: isEditMode, isSavePending: isSavePending, itemDisplayName: highlightName.trim() || `Contour at ${contourElevation || 'unknown'}`, defaultFolderColor: selectedColor, initialFolderId: currentFolderId, hideSaveLater: isServerArtifactEdit })] }))] }));
};
export default ContourHighlightDialog;
